import { CategoryFormSettings } from "../modules/shared/model";
import { escrowAPI, EscrowStatus } from "../services/escrowAPI";
import { getUserQuotes } from "../redux/Actions/QuoteActions";

/**
 * Get category form settings from Redux store
 * This should be called from within a React component that has access to useAppSelector
 */
export const getCategoryFormSettings = (): CategoryFormSettings | null => {
  // This function should be called from within a React component
  // The actual Redux selector should be used in the component
  throw new Error("getCategoryFormSettings must be called from within a React component using useAppSelector");
};


/**
 * Check if user has exceeded max revenue limit (for professionals)
 */
export const checkMaxRevenueLimit = async (userId: string, settings: CategoryFormSettings | null): Promise<{
  exceeded: boolean;
  currentTotal: number;
  limit: number;
  daysRemaining: number;
}> => {
  try {
    const maxLimit = settings?.maxRevenueLimit || 0;
    
    if (maxLimit === 0) {
      return { exceeded: false, currentTotal: 0, limit: maxLimit, daysRemaining: 0 };
    }
    
    // Get user escrows with released status
    const escrowData = await escrowAPI.getUserEscrowBalance(userId, {
      page: 1,
      limit: 100,
      filters: {
        escrowStatus: [EscrowStatus.RELEASED]
      },
      sort: [{
        orderBy: "createdAt",
        order: "ASC"
      }]
    });
    
    const escrows = escrowData.transactions || [];
    
    if (escrows.length === 0) {
      return { exceeded: false, currentTotal: 0, limit: maxLimit, daysRemaining: 365 };
    }
    
    // Get the first escrow's creation date
    const firstEscrowDate = new Date(escrows[0].createdAt);
    const currentDate = new Date();
    const daysDiff = Math.floor((currentDate.getTime() - firstEscrowDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // If more than 365 days have passed, reset the calculation
    if (daysDiff >= 365) {
      return { exceeded: false, currentTotal: 0, limit: maxLimit, daysRemaining: 365 };
    }
    
    // Calculate total released amount in the last 365 days
    const totalReleased = escrows.reduce((sum: number, escrow: any) => {
      return sum + (Number(escrow.amount) || 0);
    }, 0);
    
    const exceeded = totalReleased >= maxLimit;
    const daysRemaining = 365 - daysDiff;
    
    return { 
      exceeded, 
      currentTotal: totalReleased, 
      limit: maxLimit, 
      daysRemaining: Math.max(0, daysRemaining) 
    };
  } catch (error) {
    console.error("Error checking max revenue limit:", error);
    return { exceeded: false, currentTotal: 0, limit: 0, daysRemaining: 0 };
  }
};

/**
 * Check if user has reached min revenue threshold (for professionals)
 */
export const checkMinRevenueThreshold = async (userId: string, settings: CategoryFormSettings | null): Promise<{
  reached: boolean;
  currentTotal: number;
  threshold: number;
  daysRemaining: number;
}> => {
  try {
    const minThreshold = settings?.minRevenueLimit || 0;
    
    if (minThreshold === 0) {
      return { reached: false, currentTotal: 0, threshold: minThreshold, daysRemaining: 0 };
    }
    
    // Get user escrows with released status
    const escrowData = await escrowAPI.getUserEscrowBalance(userId, {
      page: 1,
      limit: 100,
      filters: {
        escrowStatus: [EscrowStatus.RELEASED]
      },
      sort: [{
        orderBy: "createdAt",
        order: "ASC"
      }]
    });
    
    const escrows = escrowData.transactions || [];
    
    if (escrows.length === 0) {
      return { reached: false, currentTotal: 0, threshold: minThreshold, daysRemaining: 365 };
    }
    
    // Get the first escrow's creation date
    const firstEscrowDate = new Date(escrows[0].createdAt);
    const currentDate = new Date();
    const daysDiff = Math.floor((currentDate.getTime() - firstEscrowDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // If more than 365 days have passed, reset the calculation
    if (daysDiff >= 365) {
      return { reached: false, currentTotal: 0, threshold: minThreshold, daysRemaining: 365 };
    }
    
    // Calculate total released amount in the last 365 days
    const totalReleased = escrows.reduce((sum: number, escrow: any) => {
      return sum + (Number(escrow.amount) || 0);
    }, 0);
    
    // Check if current total is between min threshold and max limit (4500-5000 range)
    const maxLimit = settings?.maxRevenueLimit || 5000;
    const reached = totalReleased >= minThreshold && totalReleased <= maxLimit;
    const daysRemaining = 365 - daysDiff;
    
    return { 
      reached, 
      currentTotal: totalReleased, 
      threshold: minThreshold, 
      daysRemaining: Math.max(0, daysRemaining) 
    };
  } catch (error) {
    console.error("Error checking min revenue threshold:", error);
    return { reached: false, currentTotal: 0, threshold: 0, daysRemaining: 0 };
  }
};

/**
 * Check if user has exceeded max purchase quote limit
 */
export const checkMaxPurchaseQuoteLimit = async (userId: string, settings: CategoryFormSettings | null): Promise<{
  exceeded: boolean;
  currentTotal: number;
  limit: number;
}> => {
  try {
    const maxLimit = settings?.maxPurchaseQuoteLimit || 0;
    
    if (maxLimit === 0) {
      return { exceeded: false, currentTotal: 0, limit: maxLimit };
    }
    
    // Get user quotes
    const quoteResponse = await getUserQuotes(userId, {
      page: 1,
      limit: 100
    });
    
    const quotes = quoteResponse.data || [];
    
    // Calculate total amount of purchased quotes
    const totalPurchased = quotes.reduce((sum: number, quote: any) => {
      // Only count quotes that have been purchased (have a price and are not free)
      if (quote.price && Number(quote.price) > 0) {
        return sum + Number(quote.price);
      }
      return sum;
    }, 0);
    
    const exceeded = totalPurchased >= maxLimit;
    
    return { 
      exceeded, 
      currentTotal: totalPurchased, 
      limit: maxLimit 
    };
  } catch (error) {
    console.error("Error checking max purchase quote limit:", error);
    return { exceeded: false, currentTotal: 0, limit: 0 };
  }
};

/**
 * Check if user has reached min purchase quote threshold
 */
export const checkMinPurchaseQuoteThreshold = async (userId: string, settings: CategoryFormSettings | null): Promise<{
  reached: boolean;
  currentTotal: number;
  threshold: number;
}> => {
  try {
    const minThreshold = settings?.minPurchaseQuoteLimit || 0;
    
    if (minThreshold === 0) {
      return { reached: false, currentTotal: 0, threshold: minThreshold };
    }
    
    // Get user quotes
    const quoteResponse = await getUserQuotes(userId, {
      page: 1,
      limit: 100
    });
    
    const quotes = quoteResponse.data || [];
    
    // Calculate total amount of purchased quotes
    const totalPurchased = quotes.reduce((sum: number, quote: any) => {
      // Only count quotes that have been purchased (have a price and are not free)
      if (quote.price && Number(quote.price) > 0) {
        return sum + Number(quote.price);
      }
      return sum;
    }, 0);
    
    const reached = totalPurchased >= minThreshold;
    
    return { 
      reached, 
      currentTotal: totalPurchased, 
      threshold: minThreshold 
    };
  } catch (error) {
    console.error("Error checking min purchase quote threshold:", error);
    return { reached: false, currentTotal: 0, threshold: 0 };
  }
};

/**
 * Check if contract response time has expired
 */
export const isContractResponseTimeExpired = (createdAt: string | Date, settings: CategoryFormSettings | null): boolean => {
  try {
    const responseTimeHours = settings?.contractResponseTime || 0;
    
    if (responseTimeHours === 0) {
      return false; // No time limit
    }
    
    const createdDate = new Date(createdAt);
    const currentDate = new Date();
    const hoursDiff = (currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
    
    return hoursDiff >= responseTimeHours;
  } catch (error) {
    console.error("Error checking contract response time:", error);
    return false;
  }
};

/**
 * Get customer tax amount for wallet funds
 */
export const getCustomerTax = (amount: number, settings: CategoryFormSettings | null): number => {
  try {
    const customerTaxPercentage = settings?.CustomerTax || 0;
    
    if (customerTaxPercentage === 0) {
      return 0;
    }
    
    return (amount * customerTaxPercentage) / 100;
  } catch (error) {
    console.error("Error calculating customer tax:", error);
    return 0;
  }
};

/**
 * Get hiring tax amount for contracts/milestones
 */
export const getHiringTax = (amount: number, settings: CategoryFormSettings | null): number => {
  try {
    const hiringTaxPercentage = settings?.HiringTax || 0;
    
    if (hiringTaxPercentage === 0) {
      return 0;
    }
    
    return (amount * hiringTaxPercentage) / 100;
  } catch (error) {
    console.error("Error calculating hiring tax:", error);
    return 0;
  }
};

/**
 * Get platform fee for quote purchase (card payment)
 */
export const getPlatformFeeForQuote = (amount: number, settings: CategoryFormSettings | null): number => {
  try {
    const platformFeePercentage = settings?.platformFeeQuote || 0;
    
    if (platformFeePercentage === 0) {
      return 0;
    }
    
    return (amount * platformFeePercentage) / 100;
  } catch (error) {
    console.error("Error calculating platform fee for quote:", error);
    return 0;
  }
};

/**
 * Get platform fee for contracts/milestones
 */
export const getPlatformFeeForContract = (amount: number, settings: CategoryFormSettings | null): number => {
  try {
    const platformFeePercentage = settings?.platformFee || 0;
    
    if (platformFeePercentage === 0) {
      return 0;
    }
    
    return (amount * platformFeePercentage) / 100;
  } catch (error) {
    console.error("Error calculating platform fee for contract:", error);
    return 0;
  }
};

/**
 * Get web work tracker fee for hourly contracts
 */
export const getWebWorkTrackerFee = (settings: CategoryFormSettings | null): number => {
  try {
    console.log("settings123", settings);
    return settings?.webWorkTrackerFee || 0;
  } catch (error) {
    console.error("Error getting web work tracker fee:", error);
    return 0;
  }
};

/**
 * Get smart tracker fee for hourly contracts (legacy)
 */
export const getSmartTrackerFee = (settings: CategoryFormSettings | null): number => {
  try {
    return settings?.smartTrackerFee || 0;
  } catch (error) {
    console.error("Error getting smart tracker fee:", error);
    return 0;
  }
};
